'use client';

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  validateSelfOnboardToken,
  type ValidateSelfOnboardResponse,
  SelfOnboardApiError,
} from '@/services/self-onboard.service';

export default function SelfOnboardLandingPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<ValidateSelfOnboardResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await validateSelfOnboardToken(token);
        if (cancelled) return;
        if (!result.valid) {
          setError('This invitation link is invalid or has expired.');
          setData(null);
          return;
        }
        setData(result);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof SelfOnboardApiError
            ? err.message
            : 'Unable to validate invitation. Please try again later.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 text-center">
        <div className="w-10 h-10 border-4 border-[var(--hrm-primary)] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-sm text-slate-500">Validating your invitation…</p>
      </div>
    );
  }

  if (error || !data?.valid) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 text-center">
        <i className="ri-error-warning-line text-4xl text-amber-500 mb-3 block" />
        <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Invitation unavailable</h1>
        <p className="text-sm text-slate-500 mb-6">{error || 'Invalid or expired invite'}</p>
        <Link to="/login" className="text-[var(--hrm-primary-dark)] font-semibold text-sm hover:underline">
          Go to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
        Welcome to {data.orgName}
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        You&apos;ve been invited to complete your employee registration.
      </p>

      <div className="space-y-2 mb-6">
        {[
          ['Work email', data.lockedFields?.workEmail],
          ['Role', data.lockedFields?.orgRoleName],
          ['Department', data.lockedFields?.departmentName],
          ['Designation', data.lockedFields?.designation],
        ].map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between rounded-xl bg-slate-50 dark:bg-slate-800/60 px-4 py-3 text-sm"
          >
            <span className="text-slate-500">{label}</span>
            <span className="font-medium text-slate-900 dark:text-white">{value}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => navigate(`/self-onboard/${token}/details`)}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] text-white font-semibold shadow-lg shadow-[var(--hrm-primary)]/20"
      >
        Continue
      </button>
    </div>
  );
}
